import { z } from 'zod';
import { staffRoleSchema } from './staff.schema';
import { taskTypeSchema } from './tasks.schema';

/** Task statuses that are open work: not finished, cancelled, or a draft nobody has submitted yet. */
export const OPEN_TASK_STATUSES_EXCLUDED = ['Completed', 'Cancelled', 'User Draft'] as const;

// --- Businesses (Figma A03.1 / A03.2) ----------------------------------------------------------------------------

export const BUSINESS_TABS = ['all', 'needs_am'] as const;
export const businessTabSchema = z.enum(BUSINESS_TABS);
export type BusinessTab = z.infer<typeof businessTabSchema>;

export const DIRECTORY_LIMIT_MAX = 50;
export const DIRECTORY_LIMIT_DEFAULT = 25;

/** "none" in `amId` means businesses with no account manager at all. */
export const listBusinessesQuerySchema = z.object({
  tab: businessTabSchema.default('all'),
  q: z.string().trim().max(100).optional(),
  amId: z.string().min(1).optional(),
  cursor: z.string().max(300).optional(),
  limit: z.coerce.number().int().min(1).max(DIRECTORY_LIMIT_MAX).default(DIRECTORY_LIMIT_DEFAULT),
});
export type ListBusinessesParams = z.output<typeof listBusinessesQuerySchema>;

export const businessRowSchema = z.object({
  id: z.string(),
  name: z.string(),
  ownerName: z.string(),
  ownerEmail: z.string(),
  city: z.string().nullable(),
  industry: z.string(),
  /** The primary account manager (or the longest-serving one when none is marked primary). Null = needs one. */
  accountManager: z.object({ id: z.string(), name: z.string() }).nullable(),
  accountManagerCount: z.number().int(),
  openTaskCount: z.number().int(),
  waitingForClientCount: z.number().int(),
  createdAt: z.string(),
});
export type BusinessRow = z.infer<typeof businessRowSchema>;

export const businessListSchema = z.object({
  items: z.array(businessRowSchema),
  nextCursor: z.string().nullable(),
  /** Tab badges, within what the caller may see. */
  counts: z.object({ all: z.number().int(), needsAm: z.number().int() }),
});
export type BusinessListResponse = z.infer<typeof businessListSchema>;

export const accountManagerAssignmentSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  isPrimary: z.boolean(),
  since: z.string(),
});

export const businessDetailSchema = businessRowSchema.extend({
  contactEmail: z.string().nullable(),
  contactPhone: z.string(),
  websiteUrl: z.string(),
  addressLine1: z.string().nullable(),
  state: z.string().nullable(),
  postalCode: z.string().nullable(),
  gstNumber: z.string().nullable(),
  googleMapsUri: z.string().nullable(),
  accountManagers: z.array(accountManagerAssignmentSchema),
  counts: z.object({
    projects: z.number().int(),
    openTasks: z.number().int(),
    doneTasks: z.number().int(),
    /** Items in the account vault: the number on the business's Deliverables tab. */
    deliverables: z.number().int(),
  }),
  /** The next few open tasks across the business's projects, soonest deadline first. */
  openWork: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      taskType: taskTypeSchema,
      status: z.string(),
      projectId: z.string(),
      projectName: z.string(),
      deadline: z.string().nullable(),
      /** Who is on it (the lead first); null = nobody yet ("Unassigned"). */
      assignee: z.string().nullable(),
    }),
  ),
  /** The people currently working on this client's open tasks. */
  workingOnIt: z.array(z.string()),
});
export type BusinessDetail = z.infer<typeof businessDetailSchema>;

/** Body of POST /api/admin/businesses/{id}/account-manager. */
export const assignAccountManagerInputSchema = z.object({
  staffId: z.string().min(1),
  /** The client sees the primary one as their main contact. The first manager a business gets is always primary. */
  makePrimary: z.boolean().default(true),
});
export type AssignAccountManagerInput = z.input<typeof assignAccountManagerInputSchema>;
export type AssignAccountManagerParams = z.output<typeof assignAccountManagerInputSchema>;

// --- Staff roster (Figma A04) -------------------------------------------------------------------------------------

export const STAFF_STATUSES = ['active', 'suspended'] as const;
export const staffStatusSchema = z.enum(STAFF_STATUSES);
export type StaffStatus = z.infer<typeof staffStatusSchema>;

export const listStaffQuerySchema = z.object({
  role: staffRoleSchema.optional(),
  status: staffStatusSchema.optional(),
  q: z.string().trim().max(100).optional(),
  cursor: z.string().max(300).optional(),
  limit: z.coerce.number().int().min(1).max(DIRECTORY_LIMIT_MAX).default(DIRECTORY_LIMIT_DEFAULT),
});
export type ListStaffParams = z.output<typeof listStaffQuerySchema>;

export const staffRowSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  staffRole: staffRoleSchema,
  title: z.string().nullable(),
  department: z.string().nullable(),
  skills: z.array(z.string()),
  maxActiveTasks: z.number().int(),
  status: staffStatusSchema,
  /** Assigned tasks that are still open. */
  activeTaskCount: z.number().int(),
  /** Businesses they are an account manager of (and how many as primary). */
  clientCount: z.number().int(),
  primaryClientCount: z.number().int(),
  /** Latest session activity; null if they have never signed in. */
  lastActiveAt: z.string().nullable(),
});
export type StaffRow = z.infer<typeof staffRowSchema>;

export const staffListSchema = z.object({
  items: z.array(staffRowSchema),
  nextCursor: z.string().nullable(),
  /** Tab badges across the whole roster, whatever the current filter. */
  counts: z.object({
    all: z.number().int(),
    admin: z.number().int(),
    account_manager: z.number().int(),
    lead_specialist: z.number().int(),
    specialist: z.number().int(),
    suspended: z.number().int(),
  }),
});
export type StaffListResponse = z.infer<typeof staffListSchema>;

export const staffDetailSchema = staffRowSchema.extend({
  createdAt: z.string(),
  twoFactorEnabled: z.boolean(),
  clients: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      city: z.string().nullable(),
      isPrimary: z.boolean(),
      since: z.string(),
      openTaskCount: z.number().int(),
    }),
  ),
  openTasks: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      taskType: taskTypeSchema,
      status: z.string(),
      deadline: z.string().nullable(),
      projectName: z.string(),
      businessName: z.string().nullable(),
    }),
  ),
  recentActivity: z.array(z.object({ id: z.string(), action: z.string(), createdAt: z.string() })),
});
export type StaffDetail = z.infer<typeof staffDetailSchema>;

/** Body of PUT /api/admin/staff/{id}: their role and the profile fields "Add staff" collects (not their email). */
export const updateStaffInputSchema = z.object({
  staffRole: staffRoleSchema,
  title: z.string().trim().max(100).optional(),
  department: z.string().trim().max(100).optional(),
  skills: z.array(z.string().trim().min(1).max(60)).max(30).default([]),
  maxActiveTasks: z.number().int().min(1).max(99),
});
export type UpdateStaffInput = z.input<typeof updateStaffInputSchema>;
export type UpdateStaffParams = z.output<typeof updateStaffInputSchema>;

/**
 * Body of POST /api/admin/staff/{id}/suspend. The reason is for admins only (it goes to the audit log).
 * TODO(api): the design offers "7 days / 30 days / Until lifted"; only "until lifted" exists, a timed lift needs a
 * stored end date and a job to lift it.
 */
export const suspendStaffInputSchema = z.object({
  reason: z.string().trim().min(3, 'Say why, for the audit log').max(500),
});
export type SuspendStaffInput = z.input<typeof suspendStaffInputSchema>;

export const suspendResultSchema = z.object({
  /** Open tasks that were handed back to Task intake because nobody else is assigned to them. */
  tasksReturnedToIntake: z.number().int(),
  /** Tasks they were removed from that still have someone else on them. */
  tasksUnassigned: z.number().int(),
});
